import { Badge, Box, Skeleton, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import Icon, { SizeVariant } from '@apigrowfinance/pbolt-icon';
import {
    ExclamationInCircleSmall,
    Statement,
    TickInCircleSmall,
} from '@apigrowfinance/pbolt-icons';
import Tag from '@apigrowfinance/pbolt-tag';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';

export interface AccountStatusPanelItemData {
    statementId: string;
    date: string;
    amountTotal: number;
    currencyCode: string;
    isPaid: boolean;
}

interface IAccountStatementsCardItem {
    itemData: AccountStatusPanelItemData;
    isLoading: boolean;
}

const StatementsPanelItem = ({
    itemData,
    isLoading,
}: IAccountStatementsCardItem) => {
    const { theme } = useTheme();

    const statementDaysToPay = dayjs().to(itemData.date);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                flex: '1 1 0',
                textAlign: 'center',
            }}>
            <Box
                sx={{
                    my: 1,
                }}>
                <Badge
                    overlap="circular"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    badgeContent={
                        !isLoading &&
                        (itemData.isPaid ? (
                            <TickInCircleSmall
                                fill={theme.palette.success.main}
                            />
                        ) : (
                            <ExclamationInCircleSmall
                                fill={theme.palette.error.dark}
                            />
                        ))
                    }>
                    <Icon
                        sizeVariant={SizeVariant.Medium}
                        isLoading={isLoading}>
                        <Statement />
                    </Icon>
                </Badge>
            </Box>
            <Box
                sx={{
                    my: 1,
                }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.primary',
                    }}
                    data-testid="date">
                    {isLoading ? (
                        <Skeleton width={75} data-testid="date-loader" />
                    ) : (
                        <DateFormatter
                            date={itemData.date}
                            format="monthYearAbr"
                        />
                    )}
                </Typography>
            </Box>
            <Box
                sx={{
                    mb: 1,
                }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: theme?.custom?.brand?.textMain || 'text.primary',
                    }}
                    data-testid="amount">
                    {isLoading ? (
                        <Skeleton width={60} data-testid="amount-loader" />
                    ) : (
                        <CurrencyFormatter
                            amount={itemData.amountTotal}
                            currency={itemData.currencyCode}
                            symbol={itemData.isPaid ? 'default' : 'default'}
                            variant={itemData.isPaid ? 'success' : 'default'}
                        />
                    )}
                </Typography>
            </Box>
            <Box
                sx={{
                    mt: 1,
                    mb: 2,
                }}>
                {isLoading ? (
                    <Skeleton width={65} data-testid="tag-loader" />
                ) : itemData.isPaid ? (
                    <Tag
                        filled
                        color="error"
                        label={
                            <FormattedMessage
                                id="AccountStatements.paid"
                                defaultMessage="Paid"
                            />
                        }
                    />
                ) : (
                    <Tag
                        filled
                        color="error"
                        label={
                            <FormattedMessage
                                id="AccountStatements.due"
                                defaultMessage="Due {message}"
                                values={{ message: statementDaysToPay }}
                            />
                        }
                    />
                )}
            </Box>
        </Box>
    );
};

export default StatementsPanelItem;
